import { use, useMemo, useState, useRef, useEffect } from 'react';
import { useUrl } from 'crossroad';
import { motion, AnimatePresence } from 'framer-motion';
import { useCultureByPassageData } from '@/data/culture/culture-by-passage-data-context';
import { CultureByBookDataContext } from '@/data/culture/culture-by-book-data-context';
import { PassageDataContext } from '@/data/passage/passage-data-context';
import { HistoricalEventsByBookDataContext } from '@/data/historical-events/historical-events-by-book-data-context';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ImportanceBadge } from '@/components/ui/importance-badge';
import { Skeleton } from '@/components/ui/skeleton';
import { SourceLinks } from '@/components/ui/source-links';
import { DatePopover } from '@/components/ui/date-popover';
import { SensoryContextCard } from './sensory-context-card';
import { DiscoveryCard } from './discovery-card';
import {
    Languages,
    Users,
    MapPin,
    GraduationCap,
    ChevronDown,
    ChevronUp,
    ChevronRight,
    ArrowRight,
    BookOpen,
    Sparkles,
    Scroll,
    Eye,
    Church,
    Building2,
    Swords,
    ExternalLink
} from 'lucide-react';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import { usePlacesByPassageData } from '@/data/places/places-by-passage-data-context';
import { usePlacesData } from '@/data/places/places-data-context';
import { getBestTransitionImageFromPlaces } from '@/data/places/place-images-fetch';
import { useHistoricalEventsByPassageData } from '@/data/historical-events/historical-events-by-passage-data-context';
import { usePeopleByPassageData } from '@/data/people/people-by-passage-data-context';
import { usePeopleData } from '@/data/people/people-data-context';
import { CulturalPointCard } from './cultural-point-card';
import { CulturalTermCard } from './cultural-term-card';
import { ScholarlyDiscussionCard } from './scholarly-discussion-card';
import { CulturalThemeCard } from './cultural-theme-card';
import { AneParallelCard } from './ane-parallel-card';
import { PersonCard } from './person-card';
import { SocialWorldCard } from './social-world-card';
import { SpecificAudienceCard } from './specific-audience-card';
import { OriginalAudienceCard } from './original-audience-card';
import { DailyLifeCard } from './daily-life-card';
import { ReligiousLifeCard } from './religious-life-card';
import { GeographicSettingCard } from './geographic-setting-card';
import { AuthorSourceCard } from './author-source-card';
import { SourceTheoryCard } from './source-theory-card';
import { AIDisclaimerBadge } from '../ui/ai-disclaimer-badge';
import { cn } from '@/lib/utils';
import peopleIllustration from '@/assets/people-background.png';
import termsIllustration from '@/assets/terms-background.png';
import culturalSettingIllustration from '@/assets/cultural-setting-background.png';
import sensoryIllustration from '@/assets/sensory-background.png';
import scholarlyThumb from '@/assets/scholarly-background.png';
import { ContextHero } from '../context/ContextHero';
import { normalizeBookName } from '@/lib/utils/bible-utils';

interface Props {
    passageId: string;
}

export default function PassageCulturalBackground({ passageId }: Props) {
    const isDarkMode = useDarkMode();
    const [, setUrl] = useUrl();
    const { data: cultureByPassage, isLoading: cultureLoading } = useCultureByPassageData(passageId);
    const cultureByBookContext = use(CultureByBookDataContext);
    const historicalEventsByBookContext = use(HistoricalEventsByBookDataContext);
    const { context: historicalEventsByPassageContext } = useHistoricalEventsByPassageData();
    const passageContext = use(PassageDataContext);
    const { getPlacesByPassageByPassageId } = usePlacesByPassageData();
    const { getPlaceByName } = usePlacesData();
    const { getPeopleByPassageByPassageId } = usePeopleByPassageData();
    const { getPersonByName } = usePeopleData();
    const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['summary', 'keyPoints', 'keyTerms', 'people', 'passageSpecificAudience', 'scholarlyDiscussion', 'originalAudience', 'ancientNearEasternParallels', 'sensoryContext']));
    const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set()); // Track which items show full description
    const [isBookLevelVisible, setIsBookLevelVisible] = useState(false);

    // Get the best transition image (same logic as animation)
    const bestImage = useMemo(() => {
        const placesByPassage = getPlacesByPassageByPassageId(passageId);
        return getBestTransitionImageFromPlaces(
            placesByPassage,
            getPlaceByName,
            historicalEventsByPassageContext?.getHistoricalEventsByPassageByPassageId,
            passageContext.get
        );
    }, [passageId, getPlacesByPassageByPassageId, getPlaceByName, historicalEventsByPassageContext, passageContext]);

    const passage = useMemo(() => {
        return passageContext.data?.items.find(p => p.id === passageId);
    }, [passageContext.data, passageId]);

    const bookBackground = useMemo(() => {
        if (!passage || !cultureByBookContext) return null;
        return cultureByBookContext.getCultureByBookByBook(passage.book);
    }, [cultureByBookContext, passage]);

    const historicalEventsByBook = useMemo(() => {
        if (!passage || !historicalEventsByBookContext) return null;
        return historicalEventsByBookContext.getHistoricalEventsByBookByBook(passage.book);
    }, [historicalEventsByBookContext, passage]);

    const bookLevelSummary = useMemo(() => {
        if (!bookBackground) return "";
        const sections = [];
        if (bookBackground.culturalThemes?.length) sections.push("Core Themes");
        if (bookBackground.ancientNearEasternParallels?.length) sections.push("ANE Parallels");
        if (historicalEventsByBook?.composition?.originalAudience || bookBackground.socialWorld || historicalEventsByBook?.composition?.originalAudience?.dailyLife || bookBackground.religiousLife || bookBackground.religiousEnvironment || bookBackground.generalGeography) {
            sections.push("Social & Religious World");
        }
        if (bookBackground.commonTerms?.length) sections.push("Lexicon");
        if (historicalEventsByBook?.composition?.authorSources || historicalEventsByBook?.composition?.sourceTheories) {
            sections.push("Composition");
        }

        if (sections.length === 0) return "";
        if (sections.length <= 4) return sections.join(" • ");
        return sections.slice(0, 4).join(" • ") + ` & more`;
    }, [bookBackground, historicalEventsByBook]);

    // Sort people by importance (high first), then alphabetically
    const sortedPeople = useMemo(() => {
        const peopleByPassage = getPeopleByPassageByPassageId(passageId);
        if (!peopleByPassage || !peopleByPassage.people || peopleByPassage.people.length === 0) {
            return [];
        }

        return [...peopleByPassage.people].sort((a, b) => {
            // First sort by importance (high -> medium -> low)
            const importanceOrder = { high: 0, medium: 1, low: 2 };
            const importanceDiff = importanceOrder[a.importance] - importanceOrder[b.importance];

            if (importanceDiff !== 0) {
                return importanceDiff;
            }

            // If importance is the same, sort alphabetically by display name or person name
            const nameA = (a.displayNameInPassage || a.personName).toLowerCase();
            const nameB = (b.displayNameInPassage || b.personName).toLowerCase();
            return nameA.localeCompare(nameB);
        });
    }, [getPeopleByPassageByPassageId, passageId]);

    // Get reconstruction image URL if available (from best image across all places)
    const reconstructionImageUrl = bestImage?.reconstructionImageUrl || null;

    const toggleSection = (section: string) => {
        setExpandedSections(prev => {
            const next = new Set(prev);
            if (next.has(section)) {
                next.delete(section);
            } else {
                next.add(section);
            }
            return next;
        });
    };

    const toggleItemExpansion = (itemId: string) => {
        setExpandedItems(prev => {
            const next = new Set(prev);
            if (next.has(itemId)) {
                next.delete(itemId);
            } else {
                next.add(itemId);
            }
            return next;
        });
    };

    const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const summaryRef = useRef<HTMLParagraphElement>(null);

    useEffect(() => {
        const checkOverflow = () => {
            if (summaryRef.current) {
                if (!isSummaryExpanded) {
                    setIsOverflowing(summaryRef.current.scrollHeight > summaryRef.current.offsetHeight);
                }
            }
        };

        checkOverflow();
        window.addEventListener('resize', checkOverflow);
        return () => window.removeEventListener('resize', checkOverflow);
    }, [isSummaryExpanded, cultureLoading, cultureByPassage]);

    if (cultureLoading || !passageContext.data) {
        return <LoadingSkeleton />;
    }

    if (!cultureByPassage) {
        return <EmptyState />;
    }

    const {
        summary,
        keyPoints,
        keyTerms,
        passageSpecificAudience,
        scholarlyDiscussion,
        sensoryContext
    } = cultureByPassage;

    return (
        <div className="space-y-4">
            {/* Cinematic Hero Card */}
            <ContextHero
                eyebrow="CULTURAL CONTEXT"
                headline="Social World & Mindsets"
                subtitle="Step into the social values, customs, and daily rhythms of the people who first heard these words."
                image={reconstructionImageUrl || culturalSettingIllustration}
                metadata={reconstructionImageUrl ? { place: bestImage?.placeName, era: bestImage?.period } : undefined}
                onClose={() => setUrl(`/app/passage/${passageId}`)}
                showClose={false}
                className="mb-8"
            />

            <div className="px-4">
                <div className="mb-8 relative group">
                    <p
                        ref={summaryRef}
                        className={cn(
                            "text-lg leading-relaxed text-foreground/80 italic border-l-2 border-primary/20 pl-6 transition-all duration-300",
                            !isSummaryExpanded && "line-clamp-3"
                        )}
                    >
                        {summary}
                    </p>
                    {(isOverflowing || isSummaryExpanded) && (
                        <button
                            onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
                            className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/60 hover:text-primary transition-colors mt-2 ml-6 flex items-center gap-1"
                        >
                            {isSummaryExpanded ? (
                                <>Show Less <ChevronUp className="h-3 w-3" /></>
                            ) : (
                                <>Show More <ChevronDown className="h-3 w-3" /></>
                            )}
                        </button>
                    )}
                </div>

                {/* Key Cultural Points Section Header */}
                {keyPoints?.length > 0 && (
                    <div className="space-y-4 relative">
                        <div className="flex items-center gap-4 mb-4">
                            <div className="space-y-1">
                                <h2 className="text-2xl font-serif font-semibold tracking-tight text-foreground/90">
                                    Ancient Setting
                                </h2>
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary/60">{keyPoints.length} Background Insights</span>
                                    <div className="h-px w-8 bg-primary/20" />
                                </div>
                            </div>
                        </div>

                        {/* Subtle background image */}
                        <div className="absolute inset-0 -mx-4 sm:-mx-6 md:-mx-8 overflow-hidden rounded-2xl pointer-events-none">
                            <div
                                className="absolute inset-0 opacity-[0.25] bg-gradient-to-l from-transparent via-transparent to-background"
                                style={{
                                    backgroundImage: `url(${culturalSettingIllustration})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'right center',
                                    maskImage: 'linear-gradient(to left, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)',
                                    WebkitMaskImage: 'linear-gradient(to left, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)'
                                }}
                            />
                        </div>

                        <div className="relative z-10 space-y-4">
                            {keyPoints.map((point, index) => {
                                const itemId = `key-point-${index}`;
                                return (
                                    <CulturalPointCard
                                        key={index}
                                        point={point}
                                        bookName={passage?.book}
                                        isExpanded={expandedItems.has(itemId)}
                                        onToggleExpansion={() => toggleItemExpansion(itemId)}
                                    />
                                );
                            })}
                        </div>
                    </div>
                )} {/* People in This Passage */}
                {sortedPeople.length > 0 && <>
                    <div className="mt-16 space-y-6 relative">
                        {/* Subtle background image */}
                        <div className="absolute inset-0 -mx-4 sm:-mx-6 md:-mx-8 overflow-hidden rounded-2xl pointer-events-none">
                            <div
                                className="absolute inset-0 opacity-[0.25] bg-gradient-to-l from-transparent via-transparent to-background"
                                style={{
                                    backgroundImage: `url(${peopleIllustration})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'right center',
                                    maskImage: 'linear-gradient(to left, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)',
                                    WebkitMaskImage: 'linear-gradient(to left, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)'
                                }}
                            />
                        </div>

                        <div className="relative z-10">
                            <div className="flex items-center gap-4">
                                <div className="p-2 rounded-lg bg-primary/5 border border-primary/10">
                                    <Users className="w-4 h-4 text-primary/70" />
                                </div>
                                <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-foreground/70">
                                    People in This Passage
                                </h3>
                                <div className="h-px flex-1 bg-gradient-to-r from-border to-transparent" />
                            </div>
                            <div className="space-y-4 mt-6">
                                {sortedPeople.map((personInPassage, index) => {
                                    const itemId = `person-${index}`;
                                    const isExpanded = expandedItems.has(itemId);
                                    const person = getPersonByName(personInPassage.personName);

                                    return (
                                        <PersonCard
                                            key={index}
                                            personInPassage={personInPassage}
                                            person={person}
                                            bookName={passage?.book}
                                            isExpanded={isExpanded}
                                            onToggleExpansion={() => toggleItemExpansion(itemId)}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </>}

                {/* Key Terms */}
                {keyTerms?.length > 0 && <>
                    <div className="mt-16 space-y-6 relative">
                        {/* Subtle background image */}
                        <div className="absolute inset-0 -mx-4 sm:-mx-6 md:-mx-8 overflow-hidden rounded-2xl pointer-events-none">
                            <div
                                className="absolute inset-0 opacity-[0.25] bg-gradient-to-l from-transparent via-transparent to-background"
                                style={{
                                    backgroundImage: `url(${termsIllustration})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'right center',
                                    maskImage: 'linear-gradient(to left, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)',
                                    WebkitMaskImage: 'linear-gradient(to left, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)'
                                }}
                            />
                        </div>

                        <div className="relative z-10">
                            <div className="flex items-center gap-4">
                                <div className="p-2 rounded-lg bg-primary/5 border border-primary/10">
                                    <Languages className="w-4 h-4 text-primary/70" />
                                </div>
                                <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-foreground/70">
                                    Key Terms
                                </h3>
                                <div className="h-px flex-1 bg-gradient-to-r from-border to-transparent" />
                            </div>
                            <div className="space-y-4 mt-6">
                                {keyTerms.map((termItem, index) => {
                                    const itemId = `keyterm-${index}`;
                                    const isExpanded = expandedItems.has(itemId);

                                    return (
                                        <CulturalTermCard
                                            key={index}
                                            term={termItem}
                                            bookName={passage?.book}
                                            isExpanded={isExpanded}
                                            onToggleExpansion={() => toggleItemExpansion(itemId)}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </>}

                {/* Passage-Specific Audience */}
                {passageSpecificAudience && (
                    <div className="mt-16 space-y-6">
                        <div className="flex items-center gap-4">
                            <div className="p-2 rounded-lg bg-primary/5 border border-primary/10">
                                <Users className="w-4 h-4 text-primary/70" />
                            </div>
                            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-foreground/70">
                                Immediate Audience Context
                            </h3>
                            <div className="h-px flex-1 bg-gradient-to-r from-border to-transparent" />
                        </div>
                        <SpecificAudienceCard
                            audience={passageSpecificAudience}
                            isExpanded={expandedSections.has('passageSpecificAudience')}
                            onToggleExpansion={() => toggleSection('passageSpecificAudience')}
                        />
                    </div>
                )}

                {/* Step Into the Scene */}
                {sensoryContext && (
                    <div className="mt-16 space-y-6 relative">
                        {/* Subtle background image */}
                        <div className="absolute inset-0 -mx-4 sm:-mx-6 md:-mx-8 overflow-hidden rounded-2xl pointer-events-none">
                            <div
                                className="absolute inset-0 opacity-[0.25] bg-gradient-to-l from-transparent via-transparent to-background"
                                style={{
                                    backgroundImage: `url(${sensoryIllustration})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'right center',
                                    maskImage: 'linear-gradient(to left, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)',
                                    WebkitMaskImage: 'linear-gradient(to left, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)'
                                }}
                            />
                        </div>

                        <div className="relative z-10">
                            <div className="flex items-center gap-4">
                                <div className="p-2 rounded-lg bg-primary/5 border border-primary/10">
                                    <Sparkles className="w-4 h-4 text-primary/70" />
                                </div>
                                <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-foreground/70">
                                    Sensory Immersions
                                </h3>
                                <div className="h-px flex-1 bg-gradient-to-r from-border to-transparent" />
                            </div>
                            <div className="mt-6">
                                <SensoryContextCard
                                    sensoryContext={sensoryContext}
                                    isExpanded={expandedSections.has('sensoryContext')}
                                    onToggleExpansion={() => toggleSection('sensoryContext')}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Scholarly Discussion */}
                {scholarlyDiscussion?.length > 0 && <>
                    <div className="mt-16 space-y-6 relative">
                        {/* Subtle background image */}
                        <div className="absolute inset-0 -mx-4 sm:-mx-6 md:-mx-8 overflow-hidden rounded-2xl pointer-events-none">
                            <div
                                className="absolute inset-0 opacity-[0.25] bg-gradient-to-l from-transparent via-transparent to-background"
                                style={{
                                    backgroundImage: `url(${scholarlyThumb})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'right center',
                                    maskImage: 'linear-gradient(to left, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)',
                                    WebkitMaskImage: 'linear-gradient(to left, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)'
                                }}
                            />
                        </div>

                        <div className="relative z-10">
                            <div className="flex items-center gap-4">
                                <div className="p-2 rounded-lg bg-primary/5 border border-primary/10">
                                    <GraduationCap className="w-4 h-4 text-primary/70" />
                                </div>
                                <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-foreground/70">
                                    Scholarly Debates
                                </h3>
                                <div className="h-px flex-1 bg-gradient-to-r from-border to-transparent" />
                            </div>
                            <div className="space-y-4 mt-6">
                                {scholarlyDiscussion.map((discussion, index) => {
                                    const itemId = `discussion-${index}`;
                                    const isExpanded = expandedItems.has(itemId);

                                    return (
                                        <ScholarlyDiscussionCard
                                            key={index}
                                            discussion={discussion}
                                            bookName={passage?.book}
                                            isExpanded={isExpanded}
                                            onToggleExpansion={() => toggleItemExpansion(itemId)}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </>}

                {/* Book-Level Transition & Section */}
                {bookBackground && (
                    <div className="pt-20">
                        {/* The Cinematic Transition Banner */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className={cn(
                                "relative overflow-hidden p-8 md:p-12 rounded-[2.5rem] border transition-all duration-500",
                                isDarkMode
                                    ? "bg-slate-900/60 border-slate-800/60 shadow-[inset_0_0_80px_rgba(0,0,0,0.3)]"
                                    : "bg-slate-50 border-slate-200/60 shadow-inner"
                            )}
                        >
                            {/* Background Reconstruction Image */}
                            {reconstructionImageUrl && (
                                <div className="absolute inset-0 z-0 opacity-20 saturate-[0.8] grayscale-[0.2]">
                                    <img
                                        src={reconstructionImageUrl}
                                        alt=""
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-r from-background via-background/40 to-transparent" />
                                </div>
                            )}

                            {/* Aesthetic BG element */}
                            <div className="absolute top-0 right-0 p-8 opacity-5 z-10">
                                <BookOpen className="w-32 h-32" />
                            </div>
                            <div className="absolute -left-12 -bottom-12 w-64 h-64 bg-primary/5 blur-[100px] rounded-full pointer-events-none" />

                            <div className="relative max-w-2xl space-y-6">
                                <div className="space-y-2">
                                    <div className="flex items-center gap-3">
                                        <span className="text-[10px] font-bold uppercase tracking-[0.4em] text-primary">The Wider Horizon</span>
                                        <div className="h-px flex-1 bg-primary/20" />
                                    </div>
                                    <h3 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground leading-tight">
                                        The World of {normalizeBookName(passage?.book)}
                                    </h3>
                                </div>

                                <p className="text-base md:text-lg leading-relaxed text-muted-foreground/90 italic font-medium">
                                    The insights above are specific to these verses. To see the full tapestry, explore the broader cultural and social record of {normalizeBookName(passage?.book)}.
                                </p>

                                {bookLevelSummary && (
                                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1.5 pt-2">
                                        <span className="text-[10px] font-bold uppercase tracking-widest text-primary/40 mb-0.5">Includes:</span>
                                        <span className="text-[11px] text-muted-foreground/60 font-medium">{bookLevelSummary}</span>
                                    </div>
                                )}

                                <div className="pt-4 flex flex-col sm:flex-row items-start sm:items-center gap-6">
                                    <Button
                                        size="lg"
                                        onClick={() => setIsBookLevelVisible(!isBookLevelVisible)}
                                        className="h-12 px-8 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase tracking-widest text-[11px] shadow-[0_0_20px_rgba(212,175,55,0.2)] hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] transition-all"
                                    >
                                        {isBookLevelVisible ? "Collapse Book Context" : `View ${normalizeBookName(passage?.book)} Context`}
                                        <ArrowRight className={cn("ml-2 h-4 w-4 transition-transform", isBookLevelVisible && "rotate-90")} />
                                    </Button>

                                    <div className="flex items-center gap-2 text-[10px] font-bold text-muted-foreground/50 uppercase tracking-widest">
                                        <Scroll className="h-3.5 w-3.5" />
                                        <span>Book-wide Background</span>
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        {/* Book-Level Content List */}
                        <AnimatePresence>
                            {isBookLevelVisible && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: "auto" }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.5, ease: "easeInOut" }}
                                    className="overflow-hidden"
                                >
                                    <div className="space-y-16 pt-16">
                                        <div className="max-w-3xl space-y-4 px-2">
                                            <div className="flex items-center gap-4 px-2">
                                                <div className="p-1.5 rounded-md bg-primary/5 border border-primary/10">
                                                    <Scroll className="w-3.5 h-3.5 text-primary/70" />
                                                </div>
                                                <h4 className="text-[10px] font-bold uppercase tracking-[0.25em] text-foreground/60">Historical Fabric</h4>
                                                <div className="h-px flex-1 bg-gradient-to-r from-border/60 to-transparent" />
                                            </div>
                                            <p className="text-lg leading-relaxed text-foreground/80 italic border-l-2 border-primary/20 pl-6">
                                                {bookBackground.summary}
                                            </p>
                                        </div>

                                        {/* Cultural Themes */}
                                        {bookBackground.culturalThemes?.length > 0 && (
                                            <div className="space-y-4">
                                                <div className="flex items-center gap-4 px-2 mb-2">
                                                    <div className="p-1.5 rounded-md bg-primary/5 border border-primary/10">
                                                        <Sparkles className="h-3.5 w-3.5 text-primary/70" />
                                                    </div>
                                                    <h3 className="text-[10px] font-bold uppercase tracking-[0.25em] text-foreground/60">
                                                        Cultural Background Themes
                                                    </h3>
                                                    <div className="h-px flex-1 bg-gradient-to-r from-border/60 to-transparent" />
                                                </div>
                                                <div className="space-y-4">
                                                    {bookBackground.culturalThemes.map((theme, index) => {
                                                        const itemId = `book-theme-${index}`;
                                                        return (
                                                            <CulturalThemeCard
                                                                key={index}
                                                                theme={theme}
                                                                isExpanded={expandedItems.has(itemId)}
                                                                onToggleExpansion={() => toggleItemExpansion(itemId)}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        {/* Ancient Near Eastern Parallels */}
                                        {bookBackground.ancientNearEasternParallels && bookBackground.ancientNearEasternParallels.length > 0 && (
                                            <div className="space-y-4">
                                                <div className="flex items-center gap-4 px-2 mb-2">
                                                    <div className="p-1.5 rounded-md bg-primary/5 border border-primary/10">
                                                        <BookOpen className="h-3.5 w-3.5 text-primary/70" />
                                                    </div>
                                                    <h3 className="text-[10px] font-bold uppercase tracking-[0.25em] text-foreground/60">
                                                        Ancient Near Eastern Parallels
                                                    </h3>
                                                    <div className="h-px flex-1 bg-gradient-to-r from-border/60 to-transparent" />
                                                </div>
                                                <div className="space-y-4">
                                                    {bookBackground.ancientNearEasternParallels.map((parallel, index) => {
                                                        const itemId = `book-ane-parallel-${index}`;
                                                        return (
                                                            <AneParallelCard
                                                                key={index}
                                                                parallel={parallel}
                                                                isExpanded={expandedItems.has(itemId)}
                                                                onToggleExpansion={() => toggleItemExpansion(itemId)}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        {/* The Social & Religious Landscape Group */}
                                        {(historicalEventsByBook?.composition?.originalAudience ||
                                            bookBackground.socialWorld ||
                                            historicalEventsByBook?.composition?.originalAudience?.dailyLife ||
                                            bookBackground.religiousLife ||
                                            bookBackground.religiousEnvironment) && (
                                                <div className="space-y-6">
                                                    <div className="flex items-center gap-4 px-2 mb-2">
                                                        <div className="p-1.5 rounded-md bg-primary/5 border border-primary/10">
                                                            <Users className="h-3.5 w-3.5 text-primary/70" />
                                                        </div>
                                                        <h3 className="text-[10px] font-bold uppercase tracking-[0.25em] text-foreground/60">The Social & Religious Landscape</h3>
                                                        <div className="h-px flex-1 bg-gradient-to-r from-border/60 to-transparent" />
                                                    </div>

                                                    <div className="space-y-4">
                                                        {/* Original Audience */}
                                                        {historicalEventsByBook?.composition?.originalAudience && (
                                                            <OriginalAudienceCard
                                                                audience={historicalEventsByBook.composition.originalAudience}
                                                                isExpanded={expandedSections.has('originalAudience')}
                                                                onToggleExpansion={() => toggleSection('originalAudience')}
                                                            />
                                                        )}

                                                        {/* Social World */}
                                                        {bookBackground.socialWorld && (
                                                            <SocialWorldCard
                                                                socialWorld={bookBackground.socialWorld}
                                                                isExpanded={expandedSections.has('socialWorld')}
                                                                onToggleExpansion={() => toggleSection('socialWorld')}
                                                            />
                                                        )}

                                                        {/* Daily Life */}
                                                        {historicalEventsByBook?.composition?.originalAudience?.dailyLife && (
                                                            <DailyLifeCard
                                                                dailyLife={historicalEventsByBook.composition.originalAudience.dailyLife}
                                                                isExpanded={expandedSections.has('dailyLife')}
                                                                onToggleExpansion={() => toggleSection('dailyLife')}
                                                            />
                                                        )}

                                                        {/* Religious Life & Environment */}
                                                        {(bookBackground.religiousLife || bookBackground.religiousEnvironment) && (
                                                            <ReligiousLifeCard
                                                                religiousLife={bookBackground.religiousLife}
                                                                religiousEnvironment={bookBackground.religiousEnvironment}
                                                                isExpanded={expandedSections.has('religiousLife')}
                                                                onToggleExpansion={() => toggleSection('religiousLife')}
                                                                expandedItems={expandedItems}
                                                                toggleItemExpansion={toggleItemExpansion}
                                                            />
                                                        )}

                                                        {/* Geographic Setting */}
                                                        {bookBackground.generalGeography && (
                                                            <GeographicSettingCard
                                                                geography={bookBackground.generalGeography}
                                                                isExpanded={expandedSections.has('bookGeography')}
                                                                onToggleExpansion={() => toggleSection('bookGeography')}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                        {/* Setting & Language Group */}
                                        {bookBackground.commonTerms && bookBackground.commonTerms.length > 0 && (
                                            <div className="space-y-6">
                                                <div className="flex items-center gap-4 px-2 mb-2">
                                                    <div className="p-1.5 rounded-md bg-primary/5 border border-primary/10">
                                                        <Languages className="h-3.5 w-3.5 text-primary/70" />
                                                    </div>
                                                    <h3 className="text-[10px] font-bold uppercase tracking-[0.25em] text-foreground/60">Important Terms</h3>
                                                    <div className="h-px flex-1 bg-gradient-to-r from-border/60 to-transparent" />
                                                </div>

                                                <div className="space-y-4">
                                                    {/* Common Vocabulary */}
                                                    {bookBackground.commonTerms && bookBackground.commonTerms.length > 0 && (
                                                        <div className="space-y-4">
                                                            {bookBackground.commonTerms.map((term, index) => {
                                                                const itemId = `book-term-${index}`;
                                                                return (
                                                                    <CulturalTermCard
                                                                        key={index}
                                                                        term={term}
                                                                        isExpanded={expandedItems.has(itemId)}
                                                                        onToggleExpansion={() => toggleItemExpansion(itemId)}
                                                                    />
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Composition */}
                                        {historicalEventsByBook?.composition?.authorSources?.length > 0 && (
                                            <div className="space-y-6">
                                                <div className="flex items-center gap-4 px-2 mb-2">
                                                    <div className="p-1.5 rounded-md bg-primary/5 border border-primary/10">
                                                        <Scroll className="h-3.5 w-3.5 text-primary/70" />
                                                    </div>
                                                    <h3 className="text-[10px] font-bold uppercase tracking-[0.25em] text-foreground/60">Author's Sources</h3>
                                                    <div className="h-px flex-1 bg-gradient-to-r from-border/60 to-transparent" />
                                                </div>

                                                <div className="space-y-4">
                                                    <div className="space-y-4">
                                                        {historicalEventsByBook.composition.authorSources.map((source, index) => {
                                                            const itemId = `book-author-source-${index}`;
                                                            return (
                                                                <AuthorSourceCard
                                                                    key={index}
                                                                    source={source}
                                                                    isExpanded={expandedItems.has(itemId)}
                                                                    onToggleExpansion={() => toggleItemExpansion(itemId)}
                                                                />
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Source Theories */}
                                        {historicalEventsByBook?.composition?.sourceTheories?.length > 0 && (
                                            <div className="space-y-6">
                                                <div className="flex items-center gap-4 px-2 mb-2">
                                                    <div className="p-1.5 rounded-md bg-primary/5 border border-primary/10">
                                                        <Scroll className="h-3.5 w-3.5 text-primary/70" />
                                                    </div>
                                                    <h3 className="text-[10px] font-bold uppercase tracking-[0.25em] text-foreground/60">Source Theories</h3>
                                                    <div className="h-px flex-1 bg-gradient-to-r from-border/60 to-transparent" />
                                                </div>

                                                <div className="space-y-4">
                                                    <div className="space-y-4 pt-4">

                                                        {historicalEventsByBook.composition.sourceTheories.map((theory, index) => {
                                                            const itemId = `book-theory-${index}`;
                                                            return (
                                                                <SourceTheoryCard
                                                                    key={index}
                                                                    theory={theory}
                                                                    isExpanded={expandedItems.has(itemId)}
                                                                    onToggleExpansion={() => toggleItemExpansion(itemId)}
                                                                />
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                )}

                {/* AI Disclaimer at the bottom */}
                <div className="py-8 border-t border-border/50 flex flex-col items-center gap-4">
                    <AIDisclaimerBadge
                        model={cultureByPassage?.generatedBy || (cultureByPassage as any)?.source}
                        passageId={passageId}
                        contextType="cultural-context"
                    />
                    <p className="text-[10px] text-muted-foreground text-center max-w-[400px]">
                        This cultural synthesis is generated by the BibleGlen Culture Module, utilizing Gemini 3.0 Pro to compile scholarly sources. Always verify findings with primary scholarly commentaries.
                    </p>
                </div>
            </div>
        </div>
    );
}

function LoadingSkeleton() {
    return (
        <div className="space-y-4 p-4">
            <Card>
                <CardContent className="space-y-2 pt-6">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                </CardContent>
            </Card>
            <Card>
                <CardContent className="pt-6">
                    <Skeleton className="h-4 w-full" />
                </CardContent>
            </Card>
        </div>
    );
}

function EmptyState() {
    return (
        <div className="flex flex-col">
            <p className="text-sm text-muted-foreground max-w-md">
                No cultural context was found for this specific passage.
            </p>
        </div>
    );
}
